import React, { useEffect, useState, useMemo } from 'react'
import { Button, Typography, Space, Badge, Avatar, Skeleton } from 'antd'
import {
  VideoCameraOutlined, PictureOutlined, RocketOutlined,
  PlayCircleOutlined, ArrowRightOutlined, CheckCircleOutlined,
  EditOutlined, BulbOutlined, FireOutlined, SafetyCertificateOutlined,
  GlobalOutlined, ThunderboltOutlined, CrownOutlined, StarOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion'
// 假设服务存在，若报错请注释
import { apiClient } from '@/services/api'
import { on as onEvent } from '@/services/events'
import { useSiteConfigStore } from '@/store/siteConfigStore'
import '@/styles/global.css'

const { Title, Paragraph, Text } = Typography

// ==========================================
// 1. 样式常量系统 (Design System)
// ==========================================

// 视频源
const HERO_VIDEOS = [
  '/assets/hero/background-1.mp4',
  '/assets/hero/background-2.mp4',
  '/assets/hero/background-3.mp4'
]

// ✨ 修改：图片源名称改成与视频一致 (background-1.jpg)
// 请确保你的 assets 文件夹里有这些 .jpg 文件
const HERO_IMAGES = [
  '/assets/hero/background-1.jpg',
  '/assets/hero/background-2.jpg',
  '/assets/hero/background-3.jpg'
]

// 渐变色定义
const GRADIENTS = {
  primary: "bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600",
  text_primary: "bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600",
  text_secondary: "bg-clip-text text-transparent bg-gradient-to-r from-purple-500 to-pink-500",
  glass_surface: "bg-white/70 backdrop-blur-xl border border-white/50 shadow-xl",
  dark_surface: "bg-slate-900 border border-slate-800",
}

// 统一按钮样式
const BUTTON_STYLES = {
  primary: `h-12 px-8 rounded-full font-bold text-base text-white border-none shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/50 hover:scale-105 active:scale-95 transition-all duration-300 ${GRADIENTS.primary}`,
  glass: "h-12 px-8 rounded-full font-bold text-base bg-white/10 backdrop-blur-md border border-white/20 text-white hover:bg-white/20 transition-all duration-300",
  outline: "h-12 px-8 rounded-full font-bold text-base border-2 border-slate-200 text-slate-700 hover:border-indigo-600 hover:text-indigo-600 bg-transparent transition-all duration-300"
}

// ==========================================
// 2. 动画组件
// ==========================================

const FadeInUp = ({ children, delay = 0, className = "" }: any) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
)

// ==========================================
// 3. 页面主组件
// ==========================================

const HomePage: React.FC = () => {
  const navigate = useNavigate()
  
  // Store & Config 容错处理
  const store = useSiteConfigStore ? useSiteConfigStore() : null
  const siteConfig = (store?.config || {}) as { siteDescription?: string }
  const loadSiteConfig = store?.load || (() => {})

  const [currentVideoIndex, setCurrentVideoIndex] = useState(0)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [isVideoReady, setIsVideoReady] = useState(false)
  const [platformStats, setPlatformStats] = useState({
    totalUsers: 0, totalGenerations: 0, satisfactionRate: 98, avgProcessingTime: 25
  })
  const [isMobile, setIsMobile] = useState(false)
  const { scrollY } = useScroll()
  
  // 视差效果
  const heroY = useTransform(scrollY, [0, 500], [0, 150])
  
  // 移动端检测
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // 数据加载
  useEffect(() => {
    loadSiteConfig()
    const fetchStats = async () => {
      try {
        if (typeof apiClient !== 'undefined') {
          const res = await apiClient.get('/public/stats')
          if (res.success && res.data) setPlatformStats(res.data)
        }
      } catch (e) { console.warn('Stats API failed, utilizing default values.') }
    }
    fetchStats()
    
    if (typeof onEvent !== 'undefined') {
      onEvent('user_updated', fetchStats)
      onEvent('video_updated', fetchStats)
    }
  }, [])

  // 视频轮播
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentVideoIndex(prev => (prev + 1) % HERO_VIDEOS.length)
    }, 8000)
    return () => clearInterval(interval)
  }, [])

  // 👇 必须添加这个 Effect 来让图片自动轮播
  useEffect(() => {
    if (isVideoReady) return // 视频播了就不换图了
    const interval = setInterval(() => {
      setCurrentImageIndex(prev => (prev + 1) % HERO_IMAGES.length)
    }, 5000)
    return () => clearInterval(interval)
  }, [isVideoReady])

  const displayStats = useMemo(() => ([
    { label: '活跃用户', value: (16800 + platformStats.totalUsers).toLocaleString(), suffix: '+' },
    { label: '生成作品', value: (548000 + platformStats.totalGenerations).toLocaleString(), suffix: '+' },
    { label: '满意度', value: platformStats.satisfactionRate, suffix: '%' },
    { label: '平均耗时', value: `<${platformStats.avgProcessingTime}`, suffix: 's' },
  ]), [platformStats])

  return (
    <div className="min-h-screen w-full bg-slate-50 overflow-x-hidden selection:bg-indigo-500 selection:text-white font-sans">
      
      {/* --- 1. HERO SECTION: 沉浸式视差首屏 --- */}
      <section className="relative h-[100dvh] flex items-center justify-center overflow-hidden bg-slate-900">
        {/* 移动端顶部Logo */}
        <div className="md:hidden absolute top-4 left-4 z-30">
          <motion.div 
            className="flex items-center gap-2"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center rounded-lg" style={{
              width: '32px',
              height: '32px'
            }}>
              <VideoCameraOutlined className="text-white text-base" />
            </div>
            <span className="text-white font-semibold text-sm" style={{ textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}>
              Sora2 AI Platform
            </span>
          </motion.div>
        </div>
        
        {/* 背景层容器 */}
        <motion.div style={{ y: heroY }} className="absolute inset-0 z-0 bg-slate-900">
          
          {/* === 层级 1: 图片幻灯片 (替补方案) === */}
          <div className="absolute inset-0 z-0">
             <AnimatePresence mode='wait'>
              {/* ✨ 关键修改：
                  这里加了一个判断：!isVideoReady
                  意思就是：只有当"视频没准备好"的时候，才渲染图片组件。
                  或者使用样式控制透明度。
              */}
              <motion.img
                key={currentImageIndex}
                src={HERO_IMAGES[currentImageIndex]}
                initial={{ opacity: 0, scale: 1.1 }} 
                
                // 🔴 修改点：互斥逻辑
                // 如果视频准备好了 (true)，图片透明度变成 0。
                // 如果视频没好 (false)，图片透明度变成 1。
                animate={{ 
                  opacity: isVideoReady ? 0 : 1, 
                  scale: 1 
                }}    
                
                transition={{ duration: 1.5 }} // 设置1.5秒的淡出过渡，避免突变         
                className="absolute inset-0 w-full h-full object-cover opacity-60" 
                alt="Hero Background"
              />
            </AnimatePresence>
            
            {/* 🔴 修改点：图片层的遮罩也要跟着图片一起消失，否则会把视频变黑 */}
            <motion.div 
              animate={{ opacity: isVideoReady ? 0 : 1 }}
              transition={{ duration: 1.5 }}
              className="absolute inset-0 bg-gradient-to-br from-indigo-950/80 via-slate-900/80 to-purple-950/80 mix-blend-multiply" 
            />
          </div>

          {/* === 层级 2: 视频层 === */}
          <AnimatePresence mode='wait'>
            <motion.video
              key={currentVideoIndex}
              src={HERO_VIDEOS[currentVideoIndex]}
              
              // 🔵 视频层逻辑保持不变：准备好了就显示
              animate={{ opacity: isVideoReady ? 1 : 0 }} 
              
              transition={{ duration: 1 }} // 视频淡入时间
              className="absolute inset-0 w-full h-full object-cover scale-105 z-[1]"
              
              playsInline 
              autoPlay 
              muted 
              loop 
              preload="auto"
              
              // 状态监听
              onPlaying={() => setIsVideoReady(true)} // 播放时 -> 图片淡出，视频淡入
              onError={() => setIsVideoReady(false)}  // 出错时 -> 视频消失，图片淡入
              onWaiting={() => setIsVideoReady(false)} // 缓冲时 -> 视频消失，图片淡入
            />
          </AnimatePresence>
          
          {/* === 层级 3: 顶层通用遮罩 (始终保留) === */}
          {/* 这个遮罩是为了保证文字清晰，无论底下是图还是视频，都需要它 */}
          <div className="absolute inset-0 bg-black/20 z-10" /> 
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-900/20 to-slate-900 z-10" />

        </motion.div>

        {/* 内容层 */}
        <div className="relative z-20 max-w-7xl mx-auto px-4 text-center">
          <FadeInUp delay={0.2}>
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-8 rounded-full bg-white/5 backdrop-blur-xl border border-white/10 shadow-2xl hover:bg-white/10 transition-colors cursor-default">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
              </span>
              <span className="text-white/90 text-sm font-medium tracking-wide">支持Sora2、Veo 3.1、Nano Banana等AI模型</span>
            </div>
          </FadeInUp>

          <FadeInUp delay={0.4}>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black mb-8 tracking-tight leading-[1.1]">
  {/* 添加外发光效果 drop-shadow-lg */}
  <span className="text-transparent bg-clip-text bg-gradient-to-br from-cyan-400 via-blue-500 to-purple-600 drop-shadow-[0_0_10px_rgba(59,130,246,0.5)]">
    创意，无界生长
  </span>
</h1>
          </FadeInUp>

          <FadeInUp delay={0.6}>
            <p className="text-lg md:text-2xl text-white/80 mb-12 max-w-2xl mx-auto font-light leading-relaxed drop-shadow-lg">
              {siteConfig?.siteDescription || '下一代 AI 内容创作平台。将您的想象力转化为令人惊叹的 4K 视频与高清图像，仅需一句话。'}
            </p>
          </FadeInUp>

          <FadeInUp delay={0.8}>
            <div className="flex flex-col sm:flex-row justify-center gap-6">
              <Button 
                onClick={() => navigate('/video')}
                className={BUTTON_STYLES.primary}
              >
                <VideoCameraOutlined /> 创作视频
              </Button>
              <Button 
                onClick={() => navigate('/image')}
                className={BUTTON_STYLES.glass}
              >
                <PictureOutlined /> 灵魂绘画
              </Button>
            </div>
          </FadeInUp>
        </div>
        
        {/* 滚动提示 */}
        <motion.div 
          className="absolute bottom-10 left-1/2 -translate-x-1/2 text-white/30"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <ArrowRightOutlined className="rotate-90 text-2xl" />
        </motion.div>
      </section>

      {/* --- 2. STATS SECTION: 悬浮数据条 --- */}
      <div className="relative z-30 -mt-24 max-w-6xl mx-auto px-4">
        <FadeInUp delay={1.0}>
          <div className={`grid grid-cols-2 md:grid-cols-4 gap-4 p-8 rounded-3xl ${GRADIENTS.glass_surface}`}>
            {displayStats.map((stat, i) => (
              <div key={i} className="text-center border-r last:border-r-0 border-slate-200/50">
                <div className="text-3xl md:text-4xl font-black text-slate-900 mb-1 tracking-tight">
                  {stat.value}<span className="text-lg text-slate-400 ml-1">{stat.suffix}</span>
                </div>
                <div className="text-xs md:text-sm text-slate-500 font-bold uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </FadeInUp>
      </div>

      {/* --- 3. MODELS: 模型矩阵 --- */}
      <section className="py-24 md:py-32 bg-slate-50 relative">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-6">内置顶级模型矩阵</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">无需切换平台，一站式调用全球最先进的生成式 AI 模型。</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: 'Veo 3.1', icon: '🎬', desc: '谷歌最新视频生成模型，超高质量输出', bg: 'bg-indigo-900' },
              { name: 'Sora2', icon: '☁️', desc: 'OpenAI 物理世界模拟引擎，光影追踪', bg: 'bg-purple-900' },
              { name: 'Nano Banana', icon: '🍌', desc: '轻量级快速图像生成，秒级响应', bg: 'bg-orange-600' },
              { name: 'Claude', icon: '🧠', desc: 'Anthropic 智能助手，提示词优化', bg: 'bg-emerald-600' }
            ].map((model, idx) => (
              <motion.div
                key={idx}
                whileHover={{ y: -10 }}
                className={`p-8 rounded-[2rem] ${model.bg} text-white relative overflow-hidden group cursor-default`}
              >
                <div className="absolute top-0 right-0 p-8 opacity-10 group-hover:opacity-20 transition-opacity text-8xl leading-none select-none">
                  {model.icon}
                </div>
                <div className="relative z-10 flex flex-col items-center text-center">
                  <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-md flex items-center justify-center text-3xl mb-6 shadow-inner mx-auto">
                    {model.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-2">{model.name}</h3>
                  <p className="text-white/80 text-sm">{model.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* --- 4. FEATURE SHOWCASE: Z-Pattern 布局 --- */}
      <div id="features" className="bg-white py-24 md:py-32 space-y-24 md:space-y-32 overflow-hidden">
        
        {/* Feature 1: Video */}
        <section className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeInUp className="relative rounded-[2.5rem] overflow-hidden shadow-2xl shadow-indigo-500/20 border border-slate-100 group">
              <div className="aspect-video bg-slate-900 relative overflow-hidden">
                <video
                  src={HERO_VIDEOS[1]}
                  className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-700"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30 group-hover:scale-110 transition-transform cursor-pointer">
                    <PlayCircleOutlined className="text-5xl text-white" />
                  </div>
                </div>
                <div className="absolute bottom-6 left-6 right-6 flex gap-3">
                  <span className="px-3 py-1 rounded-lg bg-black/50 backdrop-blur text-xs text-white border border-white/10">Sora 2.0</span>
                  <span className="px-3 py-1 rounded-lg bg-black/50 backdrop-blur text-xs text-white border border-white/10">4K 60FPS</span>
                </div>
              </div>
            </FadeInUp>

            <FadeInUp delay={0.2}>
              <Badge count={<span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-xs font-bold">New</span>} className="mb-6" />
              <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-6 leading-tight">
                文字即刻化为<br/><span className={GRADIENTS.text_primary}>影院级视频</span>
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                无需专业剪辑技能，AI 引擎能够理解复杂的物理规律与光影变化，生成连贯、逼真的视频内容。
              </p>
              <ul className="space-y-4 mb-10">
                {['文字转视频，秒级生成', '多种风格，自由选择', '角色一致性保持'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700 font-medium">
                    <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-xs">
                      <CheckCircleOutlined />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              <Button 
                onClick={() => navigate('/video')} 
                className={BUTTON_STYLES.primary}
              >
                生成视频 <ArrowRightOutlined />
              </Button>
            </FadeInUp>
          </div>
        </section>

        {/* Feature 2: Image (Reversed) */}
        <section className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeInUp className="order-2 lg:order-1">
              <Badge count={<span className="bg-pink-100 text-pink-700 px-3 py-1 rounded-full text-xs font-bold">Hot</span>} className="mb-6" />
              <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-6 leading-tight">
                每一像素都<br/><span className={GRADIENTS.text_secondary}>极致完美</span>
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                无论是写实摄影、3D 渲染还是二次元插画，AI 都能在数秒内为您呈现细节惊人的视觉作品。
              </p>
              <ul className="space-y-4 mb-10">
                {['8K 超高分辨率', '精准的光影控制', '支持图生图重绘'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700 font-medium">
                    <div className="w-6 h-6 rounded-full bg-pink-100 flex items-center justify-center text-pink-600 text-xs">
                      <CheckCircleOutlined />
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
              <Button 
                onClick={() => navigate('/image')} 
                className={`h-12 px-8 rounded-full font-bold text-base text-white border-none shadow-lg shadow-pink-500/30 hover:shadow-pink-500/50 hover:scale-105 active:scale-95 transition-all duration-300 bg-gradient-to-r from-pink-500 to-rose-500 flex items-center gap-2`}
              >
                生成图片 <ArrowRightOutlined />
              </Button>
            </FadeInUp>

            <FadeInUp delay={0.2} className="relative rounded-[2.5rem] overflow-hidden shadow-2xl shadow-pink-500/20 border border-slate-200 order-1 lg:order-2 group">
              <div className="aspect-square bg-slate-100 relative overflow-hidden">
                {/* 大背景图片层 */}
                <div className="absolute inset-0 bg-[url('/assets/img-bg.jpg')] bg-cover bg-center" />
                {/* 渐变遮罩层 - 降低不透明度，让背景图片可见 */}
                <div className="absolute inset-0 bg-gradient-to-br from-pink-100/30 to-purple-100/30" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                  <PictureOutlined className="text-9xl text-pink-200/50 group-hover:scale-110 transition-transform duration-700" />
                </div>
                {/* 模拟图片卡片堆叠 */}
                <motion.div 
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3/4 h-3/4 bg-white rounded-2xl shadow-xl p-2 rotate-[-6deg]"
                >
                  <div className="w-full h-full bg-slate-200 rounded-xl bg-[url('/assets/img-1.jpg')] bg-cover" />
                </motion.div>
                <motion.div 
                  animate={{ y: [0, -15, 0] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
                  className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3/4 h-3/4 bg-white rounded-2xl shadow-xl p-2 rotate-[3deg]"
                >
                  <div className="w-full h-full bg-slate-200 rounded-xl bg-[url('/assets/img-2.jpg')] bg-cover" />
                </motion.div>
              </div>
            </FadeInUp>
          </div>
        </section>

        {/* Feature 3: Prompt (左图右文) */}
        <section className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <FadeInUp className="relative">
              <div className="bg-white rounded-[2.5rem] shadow-2xl border border-slate-200 p-8 md:p-12 relative overflow-hidden">
                <div className="absolute top-0 right-0 p-8 opacity-[0.03]">
                  <BulbOutlined className="text-[12rem] text-orange-500" />
                </div>
                <div className="space-y-6 relative z-10">
                  <div className="bg-slate-50 p-6 rounded-2xl border border-slate-100">
                    <div className="text-xs text-slate-400 mb-2 font-mono">INPUT</div>
                    <div className="text-lg font-medium text-slate-400">一只猫</div>
                  </div>
                  <div className="flex justify-center text-orange-500 text-2xl animate-bounce">
                    <ArrowRightOutlined className="rotate-90" />
                  </div>
                  <div className="bg-orange-50 p-6 rounded-2xl border border-orange-100 shadow-sm">
                    <div className="flex justify-between items-center mb-3">
                      <div className="text-xs text-orange-600 font-bold bg-orange-100 px-2 py-1 rounded">AI OPTIMIZED</div>
                      <FireOutlined className="text-orange-500" />
                    </div>
                    <div className="text-base text-slate-800 leading-relaxed font-medium">
                      一只毛茸茸的橙色虎斑猫，有着明亮的绿色眼睛，优雅地坐在木制窗台上，沐浴在温暖的金色阳光下，背景柔和模糊，8K高清。
                    </div>
                  </div>
                </div>
              </div>
            </FadeInUp>

            <FadeInUp delay={0.2}>
              <Badge count={<span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-xs font-bold">Smart</span>} className="mb-6" />
              <h2 className="text-4xl md:text-5xl font-black text-slate-900 mb-6 leading-tight">
                让 AI 帮您<br/><span className="bg-clip-text text-transparent bg-gradient-to-r from-orange-500 to-amber-500">完善创意</span>
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                不知道如何描述您的想法？输入简单的关键词，AI会自动润色和补全您的提示词，让您的创作更加精准和专业。
              </p>
              <div className="grid grid-cols-2 gap-4 mb-10">
                <div className="flex items-center gap-3 p-4 rounded-xl bg-orange-50 border border-orange-100">
                  <ThunderboltOutlined className="text-orange-500 text-xl" />
                  <span className="font-bold text-slate-700">极速响应</span>
                </div>
                <div className="flex items-center gap-3 p-4 rounded-xl bg-orange-50 border border-orange-100">
                  <CrownOutlined className="text-orange-500 text-xl" />
                  <span className="font-bold text-slate-700">专家级优化</span>
                </div>
              </div>
              <Button 
                onClick={() => navigate('/video')} 
                className={`h-12 px-8 rounded-full font-bold text-base text-white border-none shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 hover:scale-105 active:scale-95 transition-all duration-300 bg-gradient-to-r from-orange-500 to-amber-500 flex items-center gap-2`}
              >
                优化提示词 <ArrowRightOutlined />
              </Button>
            </FadeInUp>
          </div>
        </section>
      </div>

      {/* --- 5. TESTIMONIALS --- */}
      <section className="py-24 md:py-32 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-4">创作者的声音</h2>
            <p className="text-lg text-slate-600">来自全球专业人士的真实反馈</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { name: 'Alex', role: '视觉总监', text: '彻底改变了我们的分镜流程，效率提升十倍。', avatar: '👨‍💻' },
              { name: 'Sarah', role: '自媒体人', text: '视频质量高到可以直接发布，光影细节惊人。', avatar: '👩‍🎨' },
              { name: 'David', role: '原画师', text: 'Midjourney V6 对艺术风格的理解简直是魔法。', avatar: '🎮' }
            ].map((t, idx) => (
              <FadeInUp key={idx} delay={idx * 0.1}>
                <div className="h-full p-8 rounded-3xl bg-white border border-slate-100 shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all duration-300">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center text-2xl shadow-inner border border-white">
                      {t.avatar}
                    </div>
                    <div>
                      <div className="font-bold text-slate-900 text-lg">{t.name}</div>
                      <div className="text-sm text-slate-500 font-medium">{t.role}</div>
                    </div>
                  </div>
                  <div className="flex text-amber-400 text-sm mb-4 gap-1">
                    {[1,2,3,4,5].map(i => <StarOutlined key={i} />)}
                  </div>
                  <p className="text-slate-600 italic text-lg leading-relaxed">"{t.text}"</p>
                </div>
              </FadeInUp>
            ))}
          </div>
        </div>
      </section>

      {/* --- 6. CTA: 底部召唤 --- */}
      <section className="py-24 md:py-32 relative overflow-hidden bg-slate-900">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/50 via-slate-900 to-purple-900/50" />
        <div className="absolute inset-0 bg-[url('/assets/grid-pattern.svg')] opacity-[0.05]" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <FadeInUp>
            <h2 className="text-4xl md:text-7xl font-black text-white mb-8 tracking-tight">
              准备好开启新纪元了吗？
            </h2>
            <p className="text-xl text-slate-300 mb-12 max-w-2xl mx-auto leading-relaxed">
              立即加入全球 100,000+ 创作者的行列，开启您的 AI 创作之旅。
            </p>
            
            <div className="flex justify-center mb-16">
              <Button 
                onClick={() => navigate('/video')}
                className="h-16 px-12 text-xl font-bold rounded-full bg-white text-slate-900 hover:bg-slate-100 hover:scale-105 border-none shadow-[0_0_50px_-10px_rgba(255,255,255,0.3)] transition-all flex items-center gap-3"
              >
                免费开始使用 <RocketOutlined />
              </Button>
            </div>

            <div className="flex flex-wrap justify-center gap-x-12 gap-y-4 text-slate-400 text-sm font-medium">
              <span className="flex items-center gap-2"><CheckCircleOutlined className="text-green-500" /> 无需信用卡</span>
              <span className="flex items-center gap-2"><CheckCircleOutlined className="text-green-500" /> 免费试用额度</span>
              <span className="flex items-center gap-2"><CheckCircleOutlined className="text-green-500" /> 7x24 社区支持</span>
            </div>
          </FadeInUp>
        </div>
      </section>

    </div>
  )
}

export default HomePage